import { useState } from 'react'
import { liveEntries } from '@/features/leads/workflow/timeline'
import { historyBody } from '@/features/leads/workflow/historyBody'
import { daysOverdue, overdueLabel } from '@/features/leads/workflow/overdue'
import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { useClaimLead } from '@/shared/api/leads'
import { canWorkQueue, hasPerm, useMe } from '@/shared/api/session'
import { useCanEdit, writeControlProps } from '@/shared/write-access'
import OwnershipMenu from './OwnershipMenu'
import { INTERACTION_KINDS, OUTCOMES, RATING_TIER, type OutcomeKind } from '@/shared/domain'
import type { Lead, TimelineItem } from '@/shared/types'
import { useBrand } from '@/shared/white-label/useBrand'
import { Eyebrow } from '@/ui'
import { DoNotContactSheet, useCanMarkDoNotContact } from '@/features/leads/workflow/DoNotContact'

interface Props {
  lead: Lead
}

interface LastActivity {
  verb: string
  at: string
  body?: string
  by: string
  kind: TimelineItem['kind']
  outcome?: string
}

// Timeline kinds that count as "someone worked this lead". Bookkeeping kinds
// (created, stage, handover, primary_changed) say nothing about whether the
// prospect has been contacted.

function lastActivity(lead: Lead): LastActivity | null {
  const tl = liveEntries(lead.timeline).filter(t => INTERACTION_KINDS.has(t.kind))
  const last = tl[tl.length - 1]
  if (!last) return null
  const verb =
    last.kind === 'call' ? `Samtal · ${last.outcome || 'loggat'}`
      : last.kind === 'meeting' ? 'Möte bokat'
      : last.kind === 'email' ? 'Mejl skickat'
      : 'Anteckning'
  return { verb, at: last.at, body: historyBody(last), by: last.by, kind: last.kind, outcome: last.outcome }
}

type Tone = 'yellow' | 'dark' | 'muted' | 'moss' | 'amber' | 'red' | 'neutral'

// Headline tone → left-rail color + dot color.
const TONE_RAIL: Record<Tone, string> = {
  yellow:  'before:bg-brand',
  moss:    'before:bg-moss',
  amber:   'before:bg-[#d99a3a]',
  red:     'before:bg-[var(--color-danger)]',
  dark:    'before:bg-ink-1000',
  muted:   'before:bg-ink-200',
  neutral: 'before:bg-ink-200',
}
const TONE_DOT: Record<Tone, string> = {
  yellow:  'bg-brand shadow-[0_0_0_3px_rgba(255,230,0,0.25)]',
  moss:    'bg-moss shadow-[0_0_0_3px_rgba(107,122,58,0.18)]',
  amber:   'bg-[#d99a3a] shadow-[0_0_0_3px_rgba(217,154,58,0.22)]',
  red:     'bg-[var(--color-danger)] shadow-[0_0_0_3px_rgba(196,69,47,0.18)]',
  dark:    'bg-ink-1000 shadow-[0_0_0_3px_var(--color-ink-200)]',
  muted:   'bg-ink-300 shadow-[0_0_0_3px_var(--color-ink-100)]',
  neutral: 'bg-ink-300 shadow-[0_0_0_3px_var(--color-ink-100)]',
}

type AttemptTone = 'moss' | 'amber' | 'yellow' | 'muted' | 'stone' | 'neutral'

// Outcome → chip tone. Color-codes each attempt so the BDR can read the
// pattern of the call history at a glance: did we already get voicemail,
// or are we just no-answering?
const ATTEMPT_TONE: Record<OutcomeKind, AttemptTone> = {
  reached: 'moss',
  referred: 'moss',
  voicemail: 'amber',
  booked: 'yellow',
  notfit: 'muted',
  customer: 'muted',
  bad_contact: 'stone',
  email_sent: 'neutral',
}

const ATTEMPT_CHIP: Record<AttemptTone, { chip: string; num: string }> = {
  moss:    { chip: 'border-moss',           num: 'bg-moss text-white' },
  amber:   { chip: 'border-[#d99a3a]',      num: 'bg-[#d99a3a] text-white' },
  yellow:  { chip: 'border-brand',    num: 'bg-brand text-ink-1000' },
  muted:   { chip: 'border-ink-200 text-ink-500', num: 'bg-ink-100 text-ink-700' },
  stone:   { chip: 'border-ink-300',        num: 'bg-ink-300 text-ink-1000' },
  neutral: { chip: 'border-ink-200',        num: 'bg-ink-100 text-ink-700' },
}

// Compact single-row strip — smart headline + last activity / next-up chips.
// Below it, an attempts row surfaces each individual call attempt as a chip.
export default function StatusStrip({ lead }: Props) {
  // Ownership is read here rather than drilled down from App: this component
  // sits four layers deep (App -> FocusView -> HandoverCard -> here) and
  // `useMe` is a cached query with a 5-minute staleTime, so this is a cache
  // read, not a request.
  const { data: me } = useMe()
  const { person } = useBrand()
  const claimLead = useClaimLead()
  const marked = !!lead.doNotContact
  // The server refuses a claim on a lead marked Kontakta ej.
  const canClaim = canWorkQueue(me) && !marked
  const onClaim = canClaim ? (id: string) => claimLead.mutate(id) : undefined
  // For an objection that came in some other way than a call, say by email.
  // The outcome bar has the same action for the one heard on the phone.
  const canMarkDnc = useCanMarkDoNotContact() && !marked
  const [markingDnc, setMarkingDnc] = useState(false)
  const claimPending = claimLead.isPending
  const myEmail = me?.email
  // Moving a lead is not working a lead: an administrator who is not a seller
  // must still be able to reassign one, or a lead stranded on a colleague who
  // has gone on leave is unrecoverable. Read-only mode still suppresses it —
  // handing a live lead to the wrong person is exactly the slip that guard is for.
  const canEdit = useCanEdit()
  const canMove = canEdit && (canClaim || hasPerm(me, 'manage_users'))
  const live = liveEntries(lead.timeline)
  const attempts = live.filter(t => t.kind === 'call')
  const attemptsDone = attempts.length
  const workedCount = live.filter(t => INTERACTION_KINDS.has(t.kind)).length
  const last = lastActivity(lead)
  const fb = lead.feedback

  // "What's the situation right now?" — pick a smart headline + tone.
  let headline: string | null = null
  let headlineTone: Tone = 'neutral'
  if (marked) {
    headline = 'Kontakta ej'
    headlineTone = 'red'
  } else if (lead.stage === 'Booked') {
    // Surface who the meeting is booked with (the primary contact). To change
    // it after booking, promote a different decision-maker as primary in the
    // contact card — no stage gating, so that works post-booking (RADAR-44).
    const bookedWith = lead.handover?.primary?.name
    headline = `Möte bokat${lead.meetingAt ? ' · ' + fmtDate(lead.meetingAt) : ''}`
      + (bookedWith && bookedWith !== '(unknown)' ? ` · med ${bookedWith}` : '')
    headlineTone = 'yellow'
  } else if (lead.stage === 'Handed Over') {
    headline = `Överlämnad till ${lead.seller ? person(lead.seller) : 'en säljare'}`
    headlineTone = 'dark'
  } else if (lead.stage === 'Disqualified') {
    headline = 'Avslutad — ej aktuell'
    headlineTone = 'muted'
  } else if (fb && fb.rating >= 4) {
    headline = `${RATING_TIER[fb.rating]} — kontakt, betyg ${fb.rating}★`
    headlineTone = 'moss'
  } else if (fb && fb.rating > 0) {
    headline = `Betyg ${fb.rating}★ · ${RATING_TIER[fb.rating]}`
    headlineTone = 'amber'
  } else if (lead.gotContact) {
    headline = 'Kontakt — väntar på nästa steg'
    headlineTone = 'moss'
  } else if (attemptsDone >= 3 && !lead.gotContact) {
    headline = `${attemptsDone} försök · fortfarande inget svar`
    headlineTone = 'red'
  } else if (attemptsDone > 0 && !lead.gotContact) {
    headline = `Försökt ${attemptsDone}× · ingen kontakt än`
    headlineTone = 'amber'
  }

  const ownedByMe =
    !!myEmail && !!lead.assignedTo && lead.assignedTo.toLowerCase() === myEmail.toLowerCase()
  // Worth rendering even when nothing else is: on an unclaimed lead the strip
  // carries the only Claim affordance in Focus.
  const showOwnership = !!lead.assignedTo || !!onClaim || canMove
  // The separator earns its place only when there is something to separate from.
  const dueChip =
    !lead.nextContactAt && lead.dueToday
    && lead.stage !== 'Booked' && lead.stage !== 'Handed Over'
  const hasStatusChips = !!last || !!lead.nextContactAt || dueChip
  const overdue = daysOverdue(lead.nextContactAt, Date.now())

  // Hide the strip entirely when nothing useful would render.
  const markButton = canMarkDnc && (
    <button
      type="button"
      className="text-[11px] text-ink-700 px-2 py-0.5 border border-ink-200 rounded-[2px] whitespace-nowrap bg-white cursor-pointer hover:text-ink-1000 hover:border-ink-1000"
      onClick={() => setMarkingDnc(true)}
      title="Kontakten har bett att inte bli kontaktad"
    >
      Markera Kontakta ej
    </button>
  )
  const markSheet = markingDnc && (
    <DoNotContactSheet
      lead={lead}
      mode="mark"
      onCancel={() => setMarkingDnc(false)}
      onDone={() => setMarkingDnc(false)}
    />
  )

  const hasNothingToSay =
    !headline && !last && !lead.nextContactAt && attempts.length === 0 && !showOwnership && !markButton
  if (hasNothingToSay) return null

  // Ownership is one subject, so it gets one control. A hairline separates it
  // from the status chips before it: those report on the lead, this one acts
  // on it. An unclaimed lead a seller can take is the exception — Claim is
  // the primary move there, so the state stays a plain chip rather than a
  // second dropdown saying the same word.
  const ownershipChip = showOwnership && (
    <span className={cls('flex items-center gap-1.5',
      hasStatusChips && 'ml-1 pl-2.5 border-l border-ink-200')}>
      {!lead.assignedTo && onClaim ? (
        <span
          className="text-[11px] text-ink-500 px-2 py-0.5 border border-ink-200 rounded-[2px] whitespace-nowrap bg-ink-50"
          title="I den delade poolen — den blir din en stund efter att du öppnat den"
        >
          Otilldelad
        </span>
      ) : (
        <OwnershipMenu
          leadId={lead.id}
          ownerEmail={lead.assignedTo ?? null}
          ownerSince={lead.assignedAt ?? null}
          mine={ownedByMe}
          canMove={canMove}
          canRelease={canMove && (ownedByMe || hasPerm(me, 'manage_users'))}
          workedCount={workedCount}
        />
      )}
    </span>
  )
  const claimButton = !lead.assignedTo && onClaim && (
    <button
      className="text-[11px] text-ink-1000 font-semibold px-2 py-0.5 border border-brand rounded-[2px] whitespace-nowrap bg-brand disabled:opacity-60"
      onClick={() => onClaim(lead.id)}
      {...writeControlProps(canEdit, 'Ta den här leaden — att logga ett samtal gör det här automatiskt också')}
      disabled={claimPending || !canEdit}
    >
      {claimPending ? 'Tar…' : 'Ta'}
    </button>
  )

  // Nothing to report (no headline, no last activity, no promised time, no
  // attempts) — ownership is the only reason the strip is rendering at all.
  // A full bordered section with its own 24px margin for one small chip ate
  // real estate a 1440x684 laptop doesn't have; fold it into a thin line that
  // reads as part of the header above rather than a card of its own.
  const isSparse = !headline && !hasStatusChips && attempts.length === 0
  if (isSparse) {
    return (
      <div className="flex items-center justify-end gap-1.5 px-7 pt-3 pb-1 flex-wrap">
        {ownershipChip}
        {claimButton}
        {markButton}
        {markSheet}
      </div>
    )
  }

  return (
    <section
      className={cls(
        'relative border border-ink-200 bg-white mb-6',
        'before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:transition-colors before:duration-200',
        TONE_RAIL[headlineTone],
      )}
    >
      <div className="flex flex-wrap items-center gap-4 py-2 pl-[18px] pr-3.5">
        {headline && (
          <div className="flex items-center gap-2.5 text-sm font-semibold text-ink-1000 tracking-[-0.005em] shrink-0">
            <span className={cls('w-2 h-2 rounded-full', TONE_DOT[headlineTone])} />
            <span>{headline}</span>
          </div>
        )}

        <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
          {last && (
            <span
              className="text-[11px] text-ink-700 px-2 py-0.5 border border-ink-200 rounded-[2px] whitespace-nowrap bg-white"
              title={last.body || last.verb}
            >
              {last.verb} · {fmtDate(last.at, { time: true })}
            </span>
          )}
          {lead.nextContactAt && (
            <span className={cls(
              'text-[11px] font-semibold px-2 py-0.5 border rounded-[2px] whitespace-nowrap',
              overdue > 0 ? 'border-danger text-danger bg-white' : 'border-brand text-ink-1000 bg-brand',
            )}>
              Nästa: {fmtDate(lead.nextContactAt)}{overdue > 0 && ` · ${overdueLabel(overdue)}`}
            </span>
          )}
          {dueChip && (
            <span className="text-[11px] text-ink-1000 font-semibold px-2 py-0.5 border border-brand rounded-[2px] whitespace-nowrap bg-brand">
              Dags idag
            </span>
          )}
          {ownershipChip}
          {claimButton}
          {markButton}
          {markSheet}
        </div>
      </div>

      {attempts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-3.5 pb-3 pt-2.5 pl-[22px] border-t border-ink-200 bg-paper">
          <Eyebrow as="span" size="sm" gap="none" className="mr-1">Försök</Eyebrow>
          {attempts.map((a, i) => {
            const kind = (a.outcome && a.outcome in OUTCOMES) ? (a.outcome as OutcomeKind) : null
            const tone = kind ? ATTEMPT_TONE[kind] : 'neutral'
            const label = kind ? OUTCOMES[kind].label : 'Samtal'
            const chipCls = ATTEMPT_CHIP[tone]
            const tooltip = [
              `${label} · ${fmtDate(a.at, { time: true })}`,
              historyBody(a) || null,
              a.by ? `av ${person(a.by)}` : null,
            ].filter(Boolean).join('\n')
            return (
              <span
                key={i}
                className={cls(
                  'inline-flex items-center gap-1.5 pl-1 pr-2 py-[3px] border rounded-[2px] text-[11px] font-medium text-ink-1000 bg-white whitespace-nowrap',
                  chipCls.chip,
                )}
                title={tooltip}
              >
                <span className={cls('inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-[2px] text-[10px] font-bold', chipCls.num)}>
                  {i + 1}
                </span>
                <span>{label}</span>
                <span className="text-ink-500 font-normal">· {fmtDate(a.at)}</span>
              </span>
            )
          })}
        </div>
      )}
    </section>
  )
}
